/**
 * DimensionInput — feet/inches (or decimal-feet) entry for a single dimension.
 *
 * Controlled by a decimal-feet `number` (the app's canonical/stored format) and
 * emits a decimal-feet `number` via onChange. The display switches between a
 * feet + inches pair and a single decimal field based on `mode`.
 *
 * Behaviour (matches the approved prototype):
 *  - ↑/↓ arrows, mouse wheel (while focused), and the inside ▲/▼ steppers all
 *    change the value by 1 (inches carry into feet at 11→0 / 0→11; decimal ±1 ft).
 *  - Non-numeric keystrokes are blocked at the source, so the field is never
 *    cleared by typing a letter.
 *  - Typed text is committed on blur/Enter (not per-keystroke) so the cursor is
 *    never yanked mid-edit.
 *  - On load, a stored value like 10.7 ft DISPLAYS as 10 ft 8 in but is NOT
 *    rewritten — onChange only fires on a real user edit, so untouched values
 *    are preserved exactly.
 *
 * UnitToggle is the subdued ft/in <-> decimal switch; one per group.
 */

import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import {
  UnitMode,
  round2,
  toTotalInches,
  fromTotalInches,
  clampValue,
} from '../../utils/dimensions';
import './DimensionInput.css';

type Part = 'ft' | 'in' | 'dec';

interface DimensionInputProps {
  value: number;
  onChange: (value: number) => void;
  mode: UnitMode;
  min?: number;
  max?: number;
  hasError?: boolean;
  id?: string;
  /** rendered as data-field-id on the primary (feet/decimal) input */
  fieldId?: string;
  ariaLabel?: string;
}

export const DimensionInput: React.FC<DimensionInputProps> = ({
  value,
  onChange,
  mode,
  min = 0,
  max = 999,
  hasError,
  id,
  fieldId,
  ariaLabel,
}) => {
  const ftRef = useRef<HTMLInputElement>(null);
  const inRef = useRef<HTMLInputElement>(null);
  const decRef = useRef<HTMLInputElement>(null);

  // Reflect the canonical value into any input that isn't currently focused.
  // Runs after every render, so external changes (load, toggle, step) show up
  // without clobbering text the user is actively typing.
  useEffect(() => {
    if (mode === 'decimal') {
      const el = decRef.current;
      if (el && document.activeElement !== el) el.value = value.toFixed(2);
    } else {
      const { feet, inches } = fromTotalInches(toTotalInches(value));
      if (ftRef.current && document.activeElement !== ftRef.current) {
        ftRef.current.value = String(feet);
      }
      if (inRef.current && document.activeElement !== inRef.current) {
        inRef.current.value = String(inches);
      }
    }
  });

  // Block non-numeric input at the source (typing & paste). Native beforeinput
  // is more reliable than React's synthetic onBeforeInput for inputType/data.
  useEffect(() => {
    const inputs = [ftRef.current, inRef.current, decRef.current].filter(
      Boolean,
    ) as HTMLInputElement[];
    const handler = (e: Event) => {
      const ie = e as InputEvent;
      if (!ie.inputType || ie.inputType.indexOf('insert') !== 0) return; // allow delete/etc.
      if (ie.data == null) return;
      const allowDot = mode === 'decimal';
      const ok = allowDot ? /^[0-9.]+$/.test(ie.data) : /^[0-9]+$/.test(ie.data);
      const el = e.target as HTMLInputElement;
      if (!ok) {
        e.preventDefault();
        return;
      }
      if (allowDot && ie.data.includes('.') && el.value.includes('.')) {
        e.preventDefault();
      }
    };
    inputs.forEach(i => i.addEventListener('beforeinput', handler));
    return () => inputs.forEach(i => i.removeEventListener('beforeinput', handler));
  }, [mode]);

  // Read the live decimal-feet value from the DOM (honours typed-but-uncommitted).
  const readCurrent = (): number => {
    if (mode === 'decimal') {
      return parseFloat(decRef.current?.value ?? '') || 0;
    }
    const ft = parseInt(ftRef.current?.value ?? '', 10) || 0;
    const inch = parseInt(inRef.current?.value ?? '', 10) || 0;
    return round2((ft * 12 + inch) / 12);
  };

  const writeDisplay = (v: number) => {
    if (mode === 'decimal') {
      if (decRef.current) decRef.current.value = v.toFixed(2);
    } else {
      const p = fromTotalInches(toTotalInches(v));
      if (ftRef.current) ftRef.current.value = String(p.feet);
      if (inRef.current) inRef.current.value = String(p.inches);
    }
  };

  const commit = () => {
    onChange(round2(clampValue(readCurrent(), min, max)));
  };

  const step = (part: Part, delta: number) => {
    let v: number;
    if (mode === 'decimal' || part === 'dec') {
      v = clampValue(round2(readCurrent() + delta), min, max);
    } else {
      const lo = toTotalInches(min);
      const hi = toTotalInches(max);
      let total = toTotalInches(readCurrent()) + (part === 'in' ? delta : 12 * delta);
      total = Math.min(hi, Math.max(lo, total));
      v = round2(total / 12);
    }
    writeDisplay(v); // reflect instantly even in the focused field
    onChange(v);
  };

  const keyHandler = (part: Part) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      step(part, 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      step(part, -1);
    } else if (e.key === 'Enter') {
      commit();
    }
  };

  const vstep = (part: Part) => (
    <div className="di-vstep">
      <button type="button" tabIndex={-1} aria-label="increase" onClick={() => step(part, 1)}>
        ▲
      </button>
      <button type="button" tabIndex={-1} aria-label="decrease" onClick={() => step(part, -1)}>
        ▼
      </button>
    </div>
  );

  const fieldCls = `di-field${hasError ? ' has-error' : ''}`;

  if (mode === 'decimal') {
    return (
      <div className={`${fieldCls} di-decimal`}>
        <div className="di-cell">
          <input
            ref={decRef}
            type="text"
            inputMode="decimal"
            id={id}
            defaultValue={value.toFixed(2)}
            aria-label={ariaLabel}
            data-field-id={fieldId}
            onKeyDown={keyHandler('dec')}
            onBlur={commit}
          />
          <span className="di-cap">feet</span>
          {vstep('dec')}
        </div>
      </div>
    );
  }

  const { feet, inches } = fromTotalInches(toTotalInches(value));
  return (
    <div className={fieldCls}>
      <div className="di-cell">
        <input
          ref={ftRef}
          type="text"
          inputMode="numeric"
          id={id}
          defaultValue={String(feet)}
          aria-label={ariaLabel ? `${ariaLabel} (feet)` : 'feet'}
          data-field-id={fieldId}
          onKeyDown={keyHandler('ft')}
          onBlur={commit}
        />
        <span className="di-cap">feet</span>
        {vstep('ft')}
      </div>
      <div className="di-cell">
        <input
          ref={inRef}
          type="text"
          inputMode="numeric"
          defaultValue={String(inches)}
          aria-label={ariaLabel ? `${ariaLabel} (inches)` : 'inches'}
          onKeyDown={keyHandler('in')}
          onBlur={commit}
        />
        <span className="di-cap">inches</span>
        {vstep('in')}
      </div>
    </div>
  );
};

interface CountInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  hasError?: boolean;
  id?: string;
  fieldId?: string;
  /** floating caption inside the box, e.g. "panels" */
  caption: string;
  ariaLabel?: string;
}

/** Integer count field that matches DimensionInput's look (floating caption +
 *  inside ▲/▼ stepper) but has no feet/inches or decimal toggle. Arrow keys and
 *  the steppers change by 1; non-numeric input is blocked. */
export const CountInput: React.FC<CountInputProps> = ({
  value,
  onChange,
  min = 0,
  max = 999,
  hasError,
  id,
  fieldId,
  caption,
  ariaLabel,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el) el.value = String(Math.round(value));
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e: Event) => {
      const ie = e as InputEvent;
      if (!ie.inputType || ie.inputType.indexOf('insert') !== 0) return;
      if (ie.data == null) return;
      if (!/^[0-9]+$/.test(ie.data)) e.preventDefault();
    };
    el.addEventListener('beforeinput', handler);
    return () => el.removeEventListener('beforeinput', handler);
  }, []);

  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v)));
  const readCurrent = () => parseInt(ref.current?.value ?? '', 10) || 0;
  const commit = () => onChange(clamp(readCurrent()));
  const step = (delta: number) => {
    const v = clamp(readCurrent() + delta);
    if (ref.current) ref.current.value = String(v);
    onChange(v);
  };

  return (
    <div className={`di-field di-count${hasError ? ' has-error' : ''}`}>
      <div className="di-cell">
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          id={id}
          data-field-id={fieldId}
          defaultValue={String(Math.round(value))}
          aria-label={ariaLabel}
          onKeyDown={e => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              step(1);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              step(-1);
            } else if (e.key === 'Enter') {
              commit();
            }
          }}
          onBlur={commit}
        />
        <span className="di-cap">{caption}</span>
        <div className="di-vstep">
          <button type="button" tabIndex={-1} aria-label="increase" onClick={() => step(1)}>
            ▲
          </button>
          <button type="button" tabIndex={-1} aria-label="decrease" onClick={() => step(-1)}>
            ▼
          </button>
        </div>
      </div>
    </div>
  );
};

interface UnitToggleProps {
  mode: UnitMode;
  onChange: (mode: UnitMode) => void;
  className?: string;
}

/** Subdued ft/in <-> decimal switch. Active state inherits the brand color
 *  (var(--color-primary) == retailer --partner-primary), matching the existing
 *  "Include garage door openings" toggle. One per dimension group. */
export const UnitToggle: React.FC<UnitToggleProps> = ({ mode, onChange, className }) => {
  const { t } = useTranslation('common');
  return (
  <div className={`dim-toggle${className ? ` ${className}` : ''}`}>
    <button
      type="button"
      className={`dim-toggle-lbl${mode === 'ftin' ? ' active' : ''}`}
      onClick={() => onChange('ftin')}
    >
      ft / in
    </button>
    <label className="dim-switch">
      <input
        type="checkbox"
        checked={mode === 'decimal'}
        onChange={e => onChange(e.target.checked ? 'decimal' : 'ftin')}
        aria-label={t('units.toggleDecimalFeet')}
      />
      <span className="dim-switch-track" />
      <span className="dim-switch-thumb" />
    </label>
    <button
      type="button"
      className={`dim-toggle-lbl${mode === 'decimal' ? ' active' : ''}`}
      onClick={() => onChange('decimal')}
    >
      decimal
    </button>
  </div>
  );
};

export default DimensionInput;
